import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CommandPalette, type CommandGroup, type CommandItem } from '@/lib';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import { goAlert, goAsset, goSearch } from '@/app/navigation';
import { orgRoute } from '@/app/org-route';
import { Route as SearchRoute } from '@/routes/$orgId.search';

type SearchGroups = {
  assets: { id: string; label: string; snippet?: string }[];
  sensors: { id: string; label: string; snippet?: string }[];
  alerts: { id: string; label: string; snippet?: string }[];
  sessions: { id: string; label: string; snippet?: string }[];
};

type SearchResponse = { groups: SearchGroups };

function useSearch(query: string) {
  const config = useApiConfig();
  return useQuery({
    queryKey: ['search', query],
    queryFn: async (): Promise<SearchGroups> => {
      const res = await apiFetch(
        config,
        `/v1/search?q=${encodeURIComponent(query)}`,
      );
      const body = (await res.json()) as SearchResponse;
      return body.groups;
    },
    staleTime: 5_000,
  });
}

/**
 * S9 Search. Mounts the CommandPalette component as a full-page surface
 * at /search. Selecting a result navigates to the matching detail
 * route; closing the palette routes back to /.
 */
export function S9Search() {
  const navigate = useNavigate();
  const initial = SearchRoute.useSearch();
  const [query, setQuery] = useState<string>(initial.q ?? '');

  // Reflect the live query in the URL so /search?q=… is shareable.
  useEffect(() => {
    if (query === (initial.q ?? '')) return;
    const t = setTimeout(() => goSearch(navigate, query || undefined), 200);
    return () => clearTimeout(t);
  }, [query, initial.q, navigate]);

  const results = useSearch(query);

  const groups: CommandGroup[] = useMemo(() => {
    const g = results.data;
    if (!g) return [];
    const toItems = (
      hits: SearchGroups['assets'],
      onRun: (id: string) => void,
    ): CommandItem[] =>
      hits.map((h) => ({
        id: h.id,
        label: h.label,
        hint: h.snippet,
        onRun: () => onRun(h.id),
      }));
    return [
      {
        name: 'Assets',
        items: toItems(g.assets, (id) => goAsset(navigate, id)),
      },
      {
        name: 'Sensors',
        items: toItems(g.sensors, (id) =>
          navigate({
            to: orgRoute(`/sensors/${id}`),
          } as unknown as Parameters<typeof navigate>[0]),
        ),
      },
      {
        name: 'Alerts',
        items: toItems(g.alerts, (id) => goAlert(navigate, id)),
      },
      {
        name: 'Sessions',
        items: toItems(g.sessions, () =>
          navigate({ to: orgRoute('/analyze/sessions') } as unknown as Parameters<
            typeof navigate
          >[0]),
        ),
      },
    ];
  }, [results.data, navigate]);

  return (
    <CommandPalette
      open
      query={query}
      onQueryChange={setQuery}
      onClose={() =>
        navigate({ to: '/' } as unknown as Parameters<typeof navigate>[0])
      }
      groups={groups}
    />
  );
}
